<script setup lang="ts">
import { CloseIcon, LocationIcon } from '@glow/shared/components/icons';
import CartService from './CartService.vue';
import { MasterAppointmentModal } from '@/features/appointments';
import type { RouteLocationRaw } from 'vue-router';
import { graphql, type FragmentType } from '@/gql';
import { useRoutes } from '@glow/shared/lib/routes';

const workPlaceFragment = graphql(/* GraphQL */ `
  fragment CartServiceWorkPlaceDetailedGroup_MasterWorkPlaceFragment on MasterWorkPlace {
    id
    address
    master {
      id
      slug
      fullName
      avatarUrl
    }
    company {
      id
      name
    }
    cartServices {
      id
      ...CartService_CartServiceFragment
    }
    ...CartService_MasterWorkPlaceFragment
  }
`);

const props = defineProps<{
  workPlace: FragmentType<typeof workPlaceFragment>;
}>();

const workPlace = useFragmentData(workPlaceFragment, () => props.workPlace);

const { showDeletedToast } = useContextToasts();

const isAppointmentModalVisible = ref(false);

const { getUrl } = useRoutes();
const link = computed<RouteLocationRaw>(() =>
  getUrl('partnerDetails', {
    type: 'master',
    city: 'moscow',
    partner: workPlace.value.master.slug,
  })
);

const { executeMutation: deleteWorkPlaceServices } = useMutation(
  graphql(/* GraphQL */ `
    mutation CartServiceWorkPlaceDetailedGroup_DeleteWorkPlaceServicesFromCartMutation(
      $workPlaceId: Int!
    ) {
      deleteWorkPlaceServicesFromCart(workPlaceId: $workPlaceId)
    }
  `)
);

const { executeMutation: deleteService } = useMutation(
  graphql(/* GraphQL */ `
    mutation CartServiceWorkPlaceDetailedGroup_DeleteCartServiceMutation(
      $id: Int!
    ) {
      deleteCartService(id: $id)
    }
  `)
);

async function onRemove() {
  await deleteWorkPlaceServices({ workPlaceId: workPlace.value.id });
  showDeletedToast();
}

async function onServiceRemove(id: number) {
  await deleteService({ id });
  showDeletedToast();
}
</script>

<template>
  <div class="cart-service-partner-group page-card">
    <div class="cart-service-partner-group__header">
      <NuxtLink
        class="cart-service-partner-group__partner-name-group"
        :to="link"
      >
        <AppAvatar
          :src="workPlace.master.avatarUrl"
          :name="workPlace.master.fullName"
        />
        <div class="cart-service-partner-group__partner-name">
          {{ workPlace.master.fullName }}
        </div>
      </NuxtLink>
      <AppButton
        variant="link"
        color="secondary"
        :icon-end="CloseIcon"
        @click="onRemove"
      >
        Удалить
      </AppButton>
    </div>
    <AppSeparator />
    <CartService
      v-for="service in workPlace.cartServices"
      :key="service.id"
      class="cart-service-partner-group__service"
      :cart-service="service"
      :work-place="workPlace"
      @remove="onServiceRemove(service.id)"
    />
    <div class="group-location">
      <AppIcon :icon="LocationIcon" color="accent" />
      <div class="group-location__content">
        <div class="group-location__name">
          {{ workPlace.company?.name ?? 'Личное рабочее место' }}
        </div>
        <div class="group-location__address">
          {{ workPlace.address }}
        </div>
      </div>
    </div>

    <AppButton @click="isAppointmentModalVisible = true">
      Посмотреть доступное время
    </AppButton>
  </div>
  <MasterAppointmentModal
    v-model="isAppointmentModalVisible"
    :work-place-id="workPlace.id"
  />
</template>

<style scoped lang="scss">
@import '@glow/shared/assets/core.scss';

.cart-service-partner-group {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 28px;
  padding: stretch-size(375, 1920, 16, 28);

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;

    @include media-breakpoint-down(md) {
      flex-direction: column;
      align-items: flex-start;
      gap: 20px;
    }
  }

  &__partner-name-group {
    display: flex;
    flex-wrap: nowrap;
    gap: 12px;
    align-items: center;
    color: var(--app-color-black);
  }

  &__partner-name {
    flex: 1;
    font-weight: 700;
    font-size: stretch-size(375, 1920, 17, 24);
  }

  &__service:not(:last-child) {
    border-bottom: 2px solid #e5e5e5;
    padding-bottom: 15px;
  }
}

.group-location {
  display: flex;
  align-items: center;
  gap: 8px;

  &__content {
    font-weight: 400;
    font-size: 16px;
  }

  &__address {
    color: var(--app-color-black-secondary);
  }
}
</style>
